import { SegmentedControl as MantineSegmentedControl, SegmentedControlProps, useMantineTheme } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import { usePreferencesStore } from '../../../stores';
import { pickTextColorForBackground } from '../../../utils/color';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedSegmentedControlProps extends SegmentedControlProps {
  gradientVariant?: GradientVariant;
  activeGradient?: boolean; // Apply gradient to active segment
}

const SegmentedControl = forwardRef<HTMLDivElement, ThemedSegmentedControlProps>(
  (
    {
      gradientVariant = 'primary',
      activeGradient = false,
      styles,
      ...rest
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);

    if (!activeGradient) {
      return (
        <MantineSegmentedControl
          ref={ref}
          styles={styles}
          {...rest}
        />
      );
    }

    const gradient = theme.other?.gradients?.[gradientVariant] || {
      from: theme.colors.blue[6],
      to: theme.colors.cyan[6],
    };

    // Calculate text color for active segment on gradient background
    const activeTextColor = useMemo(() => {
      const gradientString = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
      return pickTextColorForBackground(gradientString);
    }, [gradient]);

    return (
      <MantineSegmentedControl
        ref={ref}
        styles={{
          ...styles,
          indicator: {
            backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
            transition: reducedMotion ? 'none' : undefined,
            ...(styles as any)?.indicator,
          },
          label: {
            ...(styles as any)?.label,
            // Apply text color to active label
            '&[data-active]': {
              color: `${activeTextColor} !important`,
              ...(styles as any)?.label?.['&[data-active]'],
            },
          },
        }}
        {...rest}
      />
    );
  }
);

SegmentedControl.displayName = 'SegmentedControl';

export default SegmentedControl;
